import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateControlledRiskOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateControlledRiskOversightResultInput = {
  Id: string;
  Impact?: number | null;
  Likelihood?: number | null;
  Rating?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL =
  (): UpdateControlledRiskOversightResultMutation => ({
    update_risk_controlled_oversight_result: {
      __typename: 'risk_controlled_oversight_result_mutation_response',
      affected_rows: 1,
    },
  });

export const useUpdateControlledRiskOversightResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.updateControlledRiskOversightResult.mutationOptions(
      {
        onSuccess: async () => {
          await Promise.all([
            // Apollo parity: evictField(cache, 'risk_controlled_oversight_result')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.frontend.assessment.oversightResultById.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.riskOversightResultById.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightAssessmentRiskAssessmentResultsByRiskId.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.latestOversightAssessmentRiskAssessmentResultsByRiskId.pathKey(),
            }),
            // Apollo parity: evictField(cache, 'risk_controlled_oversight_result_aggregate')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
            }),
            // Apollo parity: evictField(cache, 'oversight_assessment')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityRegister.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityCount.queryKey(),
            }),
          ]);
        },
      }
    ),
  });

  return {
    updateControlledRiskOversightResult: async (
      variables: UpdateControlledRiskOversightResultInput
    ): Promise<UpdateControlledRiskOversightResultMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
